/**
 * Maps a raw chat error message onto one of the recovery states the chat
 * surface renders (design spec §11). Kept out of ChatView.jsx — a plain .js
 * file, not .jsx, with no React/CSS import — so it can be unit-tested for
 * real: this repo runs vitest with `environment: 'node'` (vitest.config.js),
 * so nothing inside ChatView.jsx itself can be rendered or imported safely.
 *
 * The `error` state this classifies is always a string: `useChat()` sets it
 * from either a WebSocket `chat:error` payload's `message` field, or from
 * `Error#message` on a REST failure (apiClient throws `new Error(body.error
 * || 'HTTP <status>')`).
 */
export function classifyError(message) {
  if (!message) return null;
  const text = String(message).toLowerCase();
  if (text === 'offline' || text.includes('nicht verbunden') || text.includes('connection lost')) return 'offline';
  // Checked before the session/token check below: the LLM guard's REST error
  // code ('quota_exceeded') and its WebSocket message ("...token budget...
  // used up") both contain the substring "token", which would otherwise be
  // caught by the sessionExpired check and shown as an expired login instead
  // of the tenant's monthly budget being spent.
  if (text.includes('quota_exceeded') || (text.includes('token budget') && text.includes('used up'))) {
    return 'quotaExceeded';
  }
  // The tenant LLM policy tightened after the session/chat was opened (WS
  // `chat:error {code, message}`, see websocket/handler.js isSessionAllowed
  // check) — checked ahead of the generic 403 branch below, same reasoning
  // as quota_exceeded above: a specific state beats the generic one.
  if (text.includes('provider_not_allowed')) return 'providerNotAllowed';
  if (text.includes('policy_unavailable')) return 'policyUnavailable';
  // A refused persona circle ("Token is not allowed…") is a permission, not
  // an expired login, although its wording contains "token".
  if (text.includes('not allowed') || text.includes('nicht freigeschaltet')) return 'permissionDenied';
  if (text.includes('401') || text.includes('unauthorized') || text.includes('token')) return 'sessionExpired';
  if (text.includes('403') || text.includes('402') || text.includes('forbidden') || text.includes('licensed')) {
    return 'permissionDenied';
  }
  return 'error';
}

/** i18n key per classifyError() result, read by ChatView.jsx. */
export const ERROR_COPY = {
  offline: 'states.offline',
  sessionExpired: 'states.sessionExpired',
  permissionDenied: 'states.permissionDenied',
  quotaExceeded: 'states.quotaExceeded',
  providerNotAllowed: 'states.providerNotAllowed',
  policyUnavailable: 'states.policyUnavailable',
  error: 'states.error',
};

/**
 * createSession() (SessionContext.jsx) failures, unlike chat errors, are
 * mostly already human-readable prose the server composed (e.g. "Gemini ist
 * nicht konfiguriert. Hinterlege ..."), so — unlike classifyError() above —
 * this must never rewrite an unrecognised message. It only intercepts the
 * handful of opaque REST error *codes* the tenant LLM policy answers with
 * (nyxCore action point a10ba442), which read as raw and unexplained
 * (`provider_not_allowed`, `policy_unavailable`) if shown as-is in a toast.
 */
export const SESSION_ERROR_COPY = {
  provider_not_allowed: 'states.providerNotAllowedHint',
  policy_unavailable: 'states.policyUnavailableHint',
};

/** Exact-match lookup only — see SESSION_ERROR_COPY. Null-safe. */
export function sessionErrorMessage(t, rawMessage) {
  if (rawMessage == null) return rawMessage;
  const key = SESSION_ERROR_COPY[rawMessage];
  return key ? t(key) : rawMessage;
}

export default classifyError;
